import React from 'react';
import { Tooltip, Typography } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/utils/localization';

interface RolesCellProps {
  roles: string[];
}

/**
 * Renders roles as plain comma-separated text per Figma design.
 * Shows the first 2 roles with a "+N more" indicator when there are more than 2.
 * The "+N more" text is wrapped in a Tooltip that displays all roles on hover.
 */
export function RolesCell({ roles }: RolesCellProps): JSX.Element | null {
  const l = useInvertedLocalize();

  if (roles.length === 0) {
    return null;
  }

  const visibleRoles = roles.slice(0, 2);
  const overflowCount = roles.length - 2;

  if (overflowCount <= 0) {
    return <Typography kind='body2'>{visibleRoles.join(', ')}</Typography>;
  }

  const allRolesText = roles.join(', ');
  const moreLabel = `+${overflowCount} ${l('more')}`;
  const visibleText = `${visibleRoles.join(', ')} `;

  // Use <div> wrapper instead of <Typography kind='body2'> (which renders <p>)
  // because Tooltip wraps children in a <div>, and <div> inside <p> is invalid HTML.
  return (
    <div>
      <Typography kind='body2'>{visibleText}</Typography>
      <Tooltip
        text={allRolesText}
        placement='bottom-start'
        arrowStaticOffset='1'>
        <Typography kind='body2'>{moreLabel}</Typography>
      </Tooltip>
    </div>
  );
}
